import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Save, CheckCircle, Clock, MessageSquare, Edit3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TaskOpenEnded = ({ task, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  // Auto-save functionality
  const saveProgress = useCallback(async (responseText) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      await apiPost(
        '/.netlify/functions/openEndedTaskApi',
        {
          sessionId: session.sessionId,
          response: responseText
        },
        user,
        userInfo?.userId
      );

      setLastSaved(new Date());
      // Don't show toast for auto-save to avoid spam
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, user, userInfo?.userId]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (responseText) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(responseText);
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing response if session has data
    if (session.sessionData?.response) {
      setResponse(session.sessionData.response);
    }
  }, [session, hasLoadedProgress]);

  const handleResponseChange = (value) => {
    setResponse(value);
    // Auto-save with debouncing
    debouncedSave(value);
  };

  const handleManualSave = async () => {
    await saveProgress(response);
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    if (!response.trim()) {
      toast.error('Please write something before completing the task');
      return;
    }

    setIsSubmitting(true);
    try {
      // First, save completion data to the session
      const completionData = {
        response: response.trim(),
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/openEndedTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        user,
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        user,
        userInfo?.userId
      );

      // Analytics tracking is now handled automatically in TaskPage
      // Pass additional properties if needed
      toast.success('Open journaling completed!');
      onComplete({ responseLength: response.trim().length });
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
      <Card variant="elevated" padding="medium" className="h-full flex flex-col">
        {/* Task Header - Simplified */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <Edit3 className="h-5 w-5 text-blue-600" />
            </div>
            <h1 className="text-base md:text-lg font-semibold text-gray-900 leading-relaxed">
              {task.description}
            </h1>
          </div>
        </div>

        {/* Response Area - Maximized to fill available space */}
        <div className="flex-1 min-h-0 mb-3 flex flex-col">
          <Input
            id="response"
            type="textarea"
            value={response}
            onChange={(e) => handleResponseChange(e.target.value)}
            placeholder="Write your response here..."
            className="resize-none text-base h-full w-full flex-1"
            wrapperClassName="flex-1 min-h-0"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-2 flex-shrink-0">
          <Button
            onClick={handleManualSave}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          <Button
            onClick={handleComplete}
            disabled={isSubmitting || !response.trim()}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Completing...' : 'Complete Journaling'}
          </Button>
        </div>

        {/* Save Status */}
        {lastSaved && (
          <div className="mt-1 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">
            <Clock className="h-3 w-3 mr-2" />
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}

        {/* Helpful Tips */}
        <Alert variant="info" className="mt-3 flex-shrink-0">
          <h3 className="text-sm font-medium text-blue-800 mb-2">💡 Writing Tips</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Write freely and honestly - there are no right or wrong answers</li>
            <li>• Your progress is automatically saved every 10 seconds</li>
            <li>• This is a safe space for your thoughts and feelings</li>
          </ul>
        </Alert>
      </Card>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskOpenEnded.Summary = ({ task, session }) => {
  const hasResponse = session?.sessionData?.response;
  const responseLength = hasResponse ? session.sessionData.response.length : 0;
  
  return (
    <div className="open-ended-summary">
      <div className="flex items-center space-x-2">
        <Edit3 className="h-4 w-4 text-blue-600" />
        <span className="text-xs text-gray-500">
          {hasResponse ? `${responseLength} characters written` : 'Not started'}
        </span>
      </div>
    </div>
  );
};

TaskOpenEnded.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData?.response) return null;

  return (
    <Card variant="elevated" padding="large">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">Open Journaling Results</h4>
      
      <div className="flex flex-col md:flex-row items-start space-y-1 md:space-y-0 md:space-x-2 mb-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Edit3 className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="text-lg font-medium text-gray-900">{task.title}</h5>
          <p className="text-sm text-gray-500">
            Completed by {patientName} on {new Date(completionData.completedAt || Date.now()).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="border-t pt-1">
        <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
          <MessageSquare className="h-4 w-4 mr-1" />
          Patient's Response:
        </h6>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600 whitespace-pre-wrap">
            {completionData.response}
          </p>
        </div>
      </div>
    </Card>
  );
};

// Add static method for task assignment form
TaskOpenEnded.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  return (
    <Card variant="default" padding="large">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instruction for Patient *
          </label>
          <Input
            type="textarea"
            value={formData.instruction || ''}
            onChange={(e) => setFormData(prev => ({...prev, instruction: e.target.value}))}
            placeholder="Enter the instruction or prompt for the patient to write about..."
            rows={3}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            This instruction will be shown to the patient as their writing prompt.
          </p>
        </div>
      </div>
    </Card>
  );
};

export default TaskOpenEnded;
